import { useId, useState, type ReactNode } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCompact, formatNumber } from '@kts/shared';
import { Card } from './Card';
import { Tabs } from './Tabs';

/**
 * Grafiklar. Ranglar tartibi rang ko'rlik (CVD) validatoridan o'tgan va qat'iy:
 * seriya rangi uning TARTIB raqamiga emas, OBYEKTIGA bog'lanadi (filtrda qayta bo'yalmaydi).
 */
export const CHART_COLORS = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
  'var(--chart-8)',
] as const;
export const CHART_OTHER = 'var(--chart-other)';

export interface Series {
  key: string;
  name: string;
  /** Aniq rang (odatda CHART_COLORS[n]); berilmasa tartib bo'yicha. */
  color?: string;
}

type Fmt = (v: number) => string;
const defaultFmt: Fmt = (v) => formatNumber(v);

interface TooltipPayloadItem {
  dataKey?: string | number;
  name?: string | number;
  value?: number | string;
  color?: string;
  payload?: Record<string, unknown>;
}

function ChartTooltip({
  active,
  payload,
  label,
  format,
  labelFormat,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  format: Fmt;
  labelFormat?: (l: string) => string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="kts-chart-tooltip">
      {label !== undefined && <div className="kts-chart-tooltip__title">{labelFormat ? labelFormat(String(label)) : label}</div>}
      {payload.map((p) => (
        <div key={String(p.dataKey ?? p.name)} className="kts-chart-tooltip__row">
          <span className="kts-chart__key" style={{ background: p.color }} />
          <span className="kts-chart-tooltip__value">{format(Number(p.value ?? 0))}</span>
          <span className="kts-chart-tooltip__name">{p.name}</span>
        </div>
      ))}
    </div>
  );
}

export function ChartLegend({ series, box }: { series: Series[]; box?: boolean }) {
  return (
    <div className="kts-chart__legend">
      {series.map((s, i) => (
        <span key={s.key} className="kts-chart__legend-item">
          <span className={box ? 'kts-chart__key kts-chart__key--box' : 'kts-chart__key'} style={{ background: s.color ?? CHART_COLORS[i % 8] }} />
          {s.name}
        </span>
      ))}
    </div>
  );
}

const axisProps = {
  tickLine: false,
  axisLine: false,
  tickMargin: 8,
} as const;

/**
 * Qiymat o'qi sanaladigan narsalar uchun (dona, buyurtma, kun): kasr belgi QO'YILMAYDI.
 * Aks holda 0..3 oralig'ida recharts 1.5 belgisini qo'yadi va formatlovchi uni "2" deb
 * yozadi — o'qda bir xil ikki yorliq paydo bo'ladi.
 */
export type AxisDecimals = { allowDecimals?: boolean };

/** "2026-10-07" → "07.10" */
export const dayTick = (v: string) => (/^\d{4}-\d{2}-\d{2}$/.test(v) ? `${v.slice(8, 10)}.${v.slice(5, 7)}` : v);

// ------------------------------------------------------------------ Maydon (vaqt bo'yicha trend)
export function AreaTrend<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height = 260,
  format = defaultFmt,
  axisFormat = (v: number) => formatCompact(v),
  xFormat = dayTick,
  stacked,
  allowDecimals,
}: {
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  height?: number;
  format?: Fmt;
  axisFormat?: Fmt;
  xFormat?: (v: string) => string;
  stacked?: boolean;
} & AxisDecimals) {
  const uid = useId().replace(/:/g, '');
  const withColors = series.map((s, i) => ({ ...s, color: s.color ?? CHART_COLORS[i % 8] }));
  return (
    <div className="kts-chart">
      {withColors.length > 1 && <ChartLegend series={withColors} />}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <defs>
            {withColors.map((s) => (
              <linearGradient key={s.key} id={`g-${uid}-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={s.color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0.02} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid vertical={false} strokeWidth={1} />
          <XAxis dataKey={xKey as never} {...axisProps} tickFormatter={xFormat} minTickGap={24} />
          <YAxis {...axisProps} width={52} allowDecimals={allowDecimals} tickFormatter={(v: number) => axisFormat(v)} />
          <Tooltip
            cursor={{ stroke: 'var(--line-strong)', strokeWidth: 1 }}
            content={(p) => <ChartTooltip {...(p as object)} format={format} labelFormat={xFormat} />}
          />
          {withColors.map((s) => (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stackId={stacked ? 'a' : undefined}
              stroke={s.color}
              strokeWidth={2}
              fill={`url(#g-${uid}-${s.key})`}
              activeDot={{ r: 5, stroke: 'var(--chart-surface)', strokeWidth: 2, fill: s.color }}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ------------------------------------------------------------------ Chiziq
export function LineTrend<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height = 260,
  format = defaultFmt,
  axisFormat = (v: number) => formatCompact(v),
  xFormat = dayTick,
  allowDecimals,
}: {
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  height?: number;
  format?: Fmt;
  axisFormat?: Fmt;
  xFormat?: (v: string) => string;
} & AxisDecimals) {
  const withColors = series.map((s, i) => ({ ...s, color: s.color ?? CHART_COLORS[i % 8] }));
  return (
    <div className="kts-chart">
      {withColors.length > 1 && <ChartLegend series={withColors} />}
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} />
          <XAxis dataKey={xKey as never} {...axisProps} tickFormatter={xFormat} minTickGap={24} />
          <YAxis {...axisProps} width={52} allowDecimals={allowDecimals} tickFormatter={(v: number) => axisFormat(v)} />
          <Tooltip
            cursor={{ stroke: 'var(--line-strong)', strokeWidth: 1 }}
            content={(p) => <ChartTooltip {...(p as object)} format={format} labelFormat={xFormat} />}
          />
          {withColors.map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 5, stroke: 'var(--chart-surface)', strokeWidth: 2, fill: s.color }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ------------------------------------------------------------------ Ustunlar
export function BarTrend<T extends Record<string, unknown>>({
  data,
  xKey,
  series,
  height = 260,
  format = defaultFmt,
  axisFormat = (v: number) => formatCompact(v),
  xFormat = dayTick,
  stacked,
  horizontal,
  allowDecimals,
}: {
  data: T[];
  xKey: keyof T & string;
  series: Series[];
  height?: number;
  format?: Fmt;
  axisFormat?: Fmt;
  xFormat?: (v: string) => string;
  stacked?: boolean;
  /** Gorizontal ustunlar (kategoriyalar uzun nomli bo'lsa). */
  horizontal?: boolean;
} & AxisDecimals) {
  const withColors = series.map((s, i) => ({ ...s, color: s.color ?? CHART_COLORS[i % 8] }));
  const last = withColors.length - 1;
  return (
    <div className="kts-chart">
      {withColors.length > 1 && <ChartLegend series={withColors} box />}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
          barCategoryGap="28%"
        >
          <CartesianGrid vertical={!!horizontal} horizontal={!horizontal} />
          {horizontal ? (
            <>
              <XAxis type="number" {...axisProps} allowDecimals={allowDecimals} tickFormatter={(v: number) => axisFormat(v)} />
              <YAxis type="category" dataKey={xKey as never} {...axisProps} width={140} tickFormatter={xFormat} />
            </>
          ) : (
            <>
              <XAxis dataKey={xKey as never} {...axisProps} tickFormatter={xFormat} minTickGap={12} />
              <YAxis {...axisProps} width={52} allowDecimals={allowDecimals} tickFormatter={(v: number) => axisFormat(v)} />
            </>
          )}
          <Tooltip
            cursor={{ fill: 'var(--accent-softer)' }}
            content={(p) => <ChartTooltip {...(p as object)} format={format} labelFormat={xFormat} />}
          />
          {withColors.map((s, i) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.name}
              fill={s.color}
              stackId={stacked ? 'a' : undefined}
              maxBarSize={24}
              stroke="var(--chart-surface)"
              strokeWidth={stacked ? 2 : 0}
              radius={
                stacked
                  ? i === last
                    ? horizontal
                      ? [0, 4, 4, 0]
                      : [4, 4, 0, 0]
                    : 0
                  : horizontal
                    ? [0, 4, 4, 0]
                    : [4, 4, 0, 0]
              }
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ------------------------------------------------------------------ Donut (qism → butun, ≤4 segment)
export interface DonutDatum {
  key: string;
  name: string;
  value: number;
  color?: string;
}

/** 4 tadan ortiq segment bo'lsa — eng kattasi 3 ta + "Boshqa" (kulrang). */
export function foldOther(data: DonutDatum[], max = 4): DonutDatum[] {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  if (sorted.length <= max) return sorted;
  const head = sorted.slice(0, max - 1);
  const rest = sorted.slice(max - 1).reduce((s, d) => s + d.value, 0);
  return [...head, { key: '__other', name: 'Boshqa', value: rest, color: CHART_OTHER }];
}

export function Donut({
  data,
  height = 220,
  format = defaultFmt,
  centerLabel,
  centerValue,
}: {
  data: DonutDatum[];
  height?: number;
  format?: Fmt;
  centerLabel?: ReactNode;
  centerValue?: ReactNode;
}) {
  const folded = foldOther(data).map((d, i) => ({ ...d, color: d.color ?? CHART_COLORS[i % 8] }));
  const total = folded.reduce((s, d) => s + d.value, 0);
  return (
    <div className="kts-chart" style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip content={(p) => <ChartTooltip {...(p as object)} format={format} />} />
            <Pie
              data={folded}
              dataKey="value"
              nameKey="name"
              innerRadius="68%"
              outerRadius="92%"
              paddingAngle={2}
              cornerRadius={4}
              stroke="var(--chart-surface)"
              strokeWidth={2}
              isAnimationActive={false}
            >
              {folded.map((d) => (
                <Cell key={d.key} fill={d.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        {(centerLabel || centerValue) && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
            <div>
              <div className="figure" style={{ fontSize: 22 }}>
                {centerValue}
              </div>
              <div style={{ fontSize: 12, color: 'var(--muted)' }}>{centerLabel}</div>
            </div>
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {folded.map((d) => (
          <div key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
            <span className="kts-chart__key kts-chart__key--box" style={{ background: d.color }} />
            <span style={{ flex: 1, color: 'var(--text-2)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</span>
            <span className="tabular" style={{ fontWeight: 700 }}>
              {format(d.value)}
            </span>
            <span className="tabular" style={{ color: 'var(--muted)', width: 44, textAlign: 'right' }}>
              {total ? Math.round((d.value / total) * 100) : 0}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Sparkline (KPI ichida)
export function Sparkline({ data, color = 'var(--chart-1)', height = 28 }: { data: number[]; color?: string; height?: number }) {
  const rows = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 3, right: 3, bottom: 3, left: 3 }}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ------------------------------------------------------------------ Grafik kartasi (Grafik | Jadval)
/**
 * Har grafikning jadval "egizagi" bor (a11y): qiymatlar tooltipsiz ham o'qiladi.
 */
export function ChartCard({
  title,
  subtitle,
  actions,
  children,
  table,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  table?: { columns: { key: string; label: string; numeric?: boolean; format?: (v: unknown) => ReactNode }[]; rows: Record<string, unknown>[] };
  className?: string;
}) {
  const [view, setView] = useState<'chart' | 'table'>('chart');
  return (
    <Card
      className={className}
      title={title}
      subtitle={subtitle}
      actions={
        <>
          {actions}
          {table && (
            <Tabs
              size="sm"
              value={view}
              onChange={setView}
              items={[
                { value: 'chart', label: 'Grafik' },
                { value: 'table', label: 'Jadval' },
              ]}
            />
          )}
        </>
      }
    >
      {view === 'chart' || !table ? (
        children
      ) : (
        <div className="kts-table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
          <table className="kts-table">
            <thead>
              <tr>
                {table.columns.map((c) => (
                  <th key={c.key} style={{ textAlign: c.numeric ? 'right' : 'left' }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i}>
                  {table.columns.map((c) => (
                    <td key={c.key} className={c.numeric ? 'num' : undefined}>
                      {c.format ? c.format(r[c.key]) : String(r[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
